import React from 'react';
import styled from 'styled-components';
import { SaiStudentFlag } from '../saiStudentDetail.types';
import { SaiStudentIndices } from '../saiStudentDetail.types';
import { FlagBreakdown } from './components/FlagBreakdown';
import { IndexPanel } from './components/IndexPanel';
import {
  PERSISTENCE_COLOR,
  PERSISTENCE_DIMENSIONS,
  PERSISTENCE_FILL,
  WELLBEING_COLOR,
  WELLBEING_DIMENSIONS,
  WELLBEING_FILL,
} from './AssessmentIndex.constants';

type AssessmentIndexWidgetProps = {
  /** Null for a run scored for flags alone, where both panels render their unscored state. */
  indices: SaiStudentIndices | null;
  /** Read separately from the scores because a run can raise flags without producing an index. */
  flags: SaiStudentFlag[];
};

export const AssessmentIndexWidget = ({
  indices,
  flags,
}: AssessmentIndexWidgetProps): React.ReactElement => {
  const wellbeingValues = WELLBEING_DIMENSIONS.map(
    ({ key }) => indices?.wellbeingDimensions[key] ?? 0
  );
  const persistenceValues = PERSISTENCE_DIMENSIONS.map(
    ({ key }) => indices?.persistenceDimensions[key] ?? 0
  );

  // A breakdown counts the flags reaching each dimension, so with nothing reaching any of them it
  // would report twelve zeros beside a table of the student's flags. Absence is not a count of none.
  const reaches = (dimensions: 'wellbeingDimensions' | 'persistenceDimensions') =>
    flags.some(flag => flag[dimensions].length > 0);

  return (
    <IndexCardsDiv>
      <IndexPanel
        title="Wellbeing Index"
        score={indices ? indices.wellbeingScore : null}
        color={WELLBEING_COLOR}
        fill={WELLBEING_FILL}
        flagBreakdown={
          reaches('wellbeingDimensions') && <FlagBreakdown kind="wellbeing" flags={flags} />
        }
        labels={WELLBEING_DIMENSIONS.map(({ label }) => label)}
        values={wellbeingValues}
      />
      <IndexPanel
        circular
        title="Persistence Index"
        score={indices ? indices.persistenceScore : null}
        color={PERSISTENCE_COLOR}
        fill={PERSISTENCE_FILL}
        flagBreakdown={
          reaches('persistenceDimensions') && <FlagBreakdown kind="persistence" flags={flags} />
        }
        labels={PERSISTENCE_DIMENSIONS.map(({ label }) => label)}
        values={persistenceValues}
      />
    </IndexCardsDiv>
  );
};

/** Below this the two index cards stack. Exported so headers that align to the second card follow it. */
export const INDEX_CARDS_STACK_BELOW = '760px';

const IndexCardsDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: ${INDEX_CARDS_STACK_BELOW}) {
    grid-template-columns: 1fr;
  }
`;
